import * as Dialog from '@radix-ui/react-dialog'
import { TCollabThread } from '@tiptap-pro/provider'
import {
  Fragment,
  KeyboardEventHandler,
  forwardRef,
  useLayoutEffect,
  useRef,
} from 'react'
import { User } from '@opallabs/concepts'
import { ZIndex } from '@opallabs/gem'
import {
  Button,
  ButtonSize,
  DropdownMenu,
  Icon,
  Tooltip,
} from '@opallabs/lapidary'
import { ThreadData } from '../CollaborationTextEditor/hooks/useThreads'
import { DocumentResourceType } from '../CollaborationTextEditor/types'
import { ContentChain } from '../TextEditor/utils'
import { Comment } from './Comment'
import { CommentEditor } from './CommentEditor'
import * as styles from './styles'
import { useThread } from './useThread'

// resourceType and resourceId are required as props when creating a new thread
// ("Add a comment..." case) because thread is undefined during thread creation
// and we need that state in order to properly construct @mentions.
export type ThreadProps = {
  thread?: TCollabThread<ThreadData>
  user?: User
  resourceType?: DocumentResourceType
  resourceId?: string
}

export const Thread = forwardRef<HTMLDivElement, ThreadProps>(
  ({ thread, user, resourceType, resourceId, ...props }, ref) => {
    const commentsRef = useRef<HTMLDivElement>(null)

    const {
      onSubmitComment,
      onResolve,
      onClose,
      onDeleteThread,
      onMentionListOpenChange,
    } = useThread()

    const handleSubmit = (content: ContentChain) => {
      onSubmitComment(content.json)
    }
    const handleResolve = () => {
      if (onResolve) {
        onResolve()
      }
    }
    const handleClose = () => {
      if (onClose) {
        onClose()
      }
    }
    const handleDeleteThread = () => {
      if (onDeleteThread) {
        onDeleteThread()
      }
    }

    const handleEscape: KeyboardEventHandler = (e) => {
      e.stopPropagation()
      onClose?.()
    }

    // Auto-scroll to the last comment when a new comment is added
    useLayoutEffect(() => {
      if (commentsRef.current && thread?.comments.length) {
        commentsRef.current.scrollTo({
          top: commentsRef.current.scrollHeight,
        })
      }
    }, [thread?.comments.length])

    return (
      <div
        className="editor-thread"
        css={styles.container(!!thread && thread.comments.length > 0)}
        {...props}
        ref={ref}
      >
        {thread && thread.comments.length > 0 ? (
          <Fragment>
            <div css={styles.threadHeader}>
              <Dialog.Title asChild>
                <span>Thread</span>
              </Dialog.Title>
              <div css={styles.headerActions}>
                <Tooltip
                  content="Resolve Thread"
                  zIndex={ZIndex.Shroud.Tooltip}
                >
                  <Button
                    variant="ghost"
                    name="resolve thread"
                    icon={<Icon glyph="gem-check-circle" />}
                    css={styles.headerButton}
                    size={ButtonSize.Small}
                    onClick={handleResolve}
                  />
                </Tooltip>
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger asChild>
                    <Button
                      variant="ghost"
                      name="thread options"
                      icon={<Icon glyph="gem-more-horizontal" />}
                      css={styles.headerButton}
                      size={ButtonSize.Small}
                    />
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Portal>
                    <DropdownMenu.Content align="start" css={styles.optionMenu}>
                      <DropdownMenu.Item
                        css={styles.optionMenuItem}
                        onSelect={() =>
                          navigator.clipboard.writeText(
                            window.top?.location.href || window.location.href
                          )
                        }
                      >
                        <Icon glyph="gem-copy" ariaHidden />
                        Copy Link to Thread
                      </DropdownMenu.Item>
                      {thread.data.author_id === user?.uuid && (
                        <DropdownMenu.Item
                          css={styles.optionMenuItem}
                          destructive={true}
                          onSelect={handleDeleteThread}
                        >
                          <Icon glyph="gem-trash" ariaHidden />
                          Delete Thread
                        </DropdownMenu.Item>
                      )}
                    </DropdownMenu.Content>
                  </DropdownMenu.Portal>
                </DropdownMenu.Root>
                <Button
                  variant="ghost"
                  name="close thread"
                  icon={<Icon glyph="gem-close" />}
                  css={styles.headerButton}
                  size={ButtonSize.Small}
                  onClick={handleClose}
                />
              </div>
            </div>
            <div css={styles.content} ref={commentsRef}>
              {thread.comments.map((c) => (
                <Comment key={c.id} {...c} user={user} />
              ))}
            </div>
            <div css={styles.footer}>
              <div css={styles.textAndActionsContainer}>
                <CommentEditor
                  placeholder="Reply to thread..."
                  onEscape={handleEscape}
                  onSubmit={handleSubmit}
                  threadResourceType={thread?.data.resource_type}
                  threadResourceId={thread?.data.resource_id}
                />
              </div>
            </div>
          </Fragment>
        ) : (
          <div css={styles.textAndActionsContainer}>
            <CommentEditor
              autofocus
              onEscape={handleEscape}
              onMentionListOpenChange={onMentionListOpenChange}
              onSubmit={handleSubmit}
              placeholder="Add a comment..."
              threadResourceType={resourceType}
              threadResourceId={resourceId}
            />
          </div>
        )}
      </div>
    )
  }
)

Thread.displayName = 'Thread'
